import { useQuery } from '@tanstack/react-query';

import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import type { UntrackedExposure } from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

import { useTradingStore } from '../store';

/** Slow by design: this is a safety net, not a live readout. */
export const EXPOSURE_REFETCH_INTERVAL_MS = 60_000;
/** Short enough that a remount after a close re-reads, long enough to dedupe. */
export const EXPOSURE_STALE_TIME_MS = 10_000;

/**
 * What Hyperliquid is holding for this user that NO trade row explains.
 *
 * The reported incident: a partially filled GTC short left 274 units resting
 * after the filled 22 were closed; the remainder later filled into a position
 * nothing tracked, so the terminal showed nothing while ~$50 of the balance was
 * held, and the orphan was liquidated. The server does the matching (this hook
 * classifies nothing); the FE's job is to show it and offer the two moves that
 * free the money.
 *
 * Enabled on the same terms as `useActiveUserTrades` — real mode, a token and a
 * factions client — so demo mode never asks. Push events and every close /
 * cancel invalidate it; the interval is only the backstop for exposure that
 * appears with no local action at all (a resting order filling on its own),
 * which is exactly how the incident started.
 */
export function useUntrackedExposure() {
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const token = useTradingStore((s) => s.token);
    const networkId = useTokenPairStore((s) => s.networkId);
    // read-only, so it comes off factions' speed facet like the trade lists (V1).
    const backend = useSpeedBackend();

    const enabled =
        isRealMode && !!token && backend !== null && networkId !== null;

    return useQuery<UntrackedExposure | null>({
        queryKey: queryKeys.trades.exposure(networkId ?? 0),
        queryFn: async ({ signal }) => {
            if (!backend || networkId === null) return null;
            return backend.fetchExposure({ networkId, signal });
        },
        enabled,
        staleTime: EXPOSURE_STALE_TIME_MS,
        refetchInterval: EXPOSURE_REFETCH_INTERVAL_MS,
        // a user who tabs back after a fill is exactly who needs to see this.
        refetchOnWindowFocus: true,
    });
}
